"use client";

import { c as _c } from "react/compiler-runtime";
import { Button } from "@/components/rac/button";
import { Tooltip, TooltipTrigger } from "@/components/rac/tooltip";
import { logger } from "@/utils/logger";
import { ControlPosition, MapControl, useMap } from "@vis.gl/react-google-maps";
import { Eye, Map as MapIcon, MapPin, Maximize, MoreHorizontal, Satellite, ZoomIn, ZoomOut } from "lucide-react";
import { useEffect, useState } from "react";
interface MapControlsFABProps {
  className?: string;
  buttonClassName?: string;
  fabClassName?: string;
}
export function MapControlsFAB(t0) {
  const $ = _c(43);
  const {
    className,
    buttonClassName,
    fabClassName
  } = t0;
  const map = useMap();
  const [isExpanded, setIsExpanded] = useState(false);
  const [currentMapType, setCurrentMapType] = useState("roadmap");
  const [isFullscreen, setIsFullscreen] = useState(false);
  const buttonClasses = buttonClassName || "bg-white text-gray-700 rounded-full shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105 active:scale-95 disabled:opacity-50 w-8 h-8";
  const fabClasses = fabClassName || "bg-black text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 active:scale-95 w-8 h-8 data-[hovered]:bg-black data-[hovered]:text-white";
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const handleFullscreenChange = () => {
        setIsFullscreen(!!document.fullscreenElement);
      };
      document.addEventListener("fullscreenchange", handleFullscreenChange);
      return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  let t3;
  if ($[2] !== currentMapType || $[3] !== map) {
    t3 = () => {
      if (!map) {
        return;
      }
      const types = ["roadmap", "satellite", "hybrid", "terrain"];
      const currentIndex = types.indexOf(currentMapType);
      const nextIndex = (currentIndex + 1) % types.length;
      const newType = types[nextIndex];
      map.setMapTypeId(newType);
      setCurrentMapType(newType);
    };
    $[2] = currentMapType;
    $[3] = map;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const toggleMapType = t3;
  let t4;
  if ($[5] !== map) {
    t4 = () => {
      if (!map) {
        return;
      }
      const mapContainer = map.getDiv();
      if (mapContainer) {
        const fullscreenElement = document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement || document.msFullscreenElement;
        if (fullscreenElement) {
          if (document.exitFullscreen) {
            document.exitFullscreen();
          } else {
            if ((document as any).webkitExitFullscreen) {
              (document as any).webkitExitFullscreen();
            } else {
              if ((document as any).mozCancelFullScreen) {
                (document as any).mozCancelFullScreen();
              } else {
                if ((document as any).msExitFullscreen) {
                  (document as any).msExitFullscreen();
                }
              }
            }
          }
        } else {
          if (typeof mapContainer.requestFullscreen === "function") {
            mapContainer.requestFullscreen();
          } else {
            if (typeof (mapContainer as any).webkitRequestFullscreen === "function") {
              (mapContainer as any).webkitRequestFullscreen();
            } else {
              if (typeof (mapContainer as any).mozRequestFullScreen === "function") {
                (mapContainer as any).mozRequestFullScreen();
              } else {
                if (typeof (mapContainer as any).msRequestFullscreen === "function") {
                  (mapContainer as any).msRequestFullscreen();
                } else {
                  if (typeof (mapContainer as any).webkitEnterFullscreen === "function") {}
                }
              }
            }
          }
        }
      }
    };
    $[5] = map;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const toggleFullscreen = t4;
  let t5;
  if ($[7] !== map) {
    t5 = () => {
      if (!map) {
        return;
      }
      const currentZoom = map.getZoom();
      if (currentZoom !== undefined) {
        map.setZoom(currentZoom + 1);
      }
    };
    $[7] = map;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const zoomIn = t5;
  let t6;
  if ($[9] !== map) {
    t6 = () => {
      if (!map) {
        return;
      }
      const currentZoom_0 = map.getZoom();
      if (currentZoom_0 !== undefined) {
        map.setZoom(currentZoom_0 - 1);
      }
    };
    $[9] = map;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const zoomOut = t6;
  let t7;
  if ($[11] !== map) {
    t7 = () => {
      if (!map) {
        return;
      }
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(position => {
          const {
            latitude,
            longitude
          } = position.coords;
          map.setCenter({
            lat: latitude,
            lng: longitude
          });
          map.setZoom(15);
        }, _temp);
      }
    };
    $[11] = map;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const centerOnLocation = t7;
  const t8 = `flex flex-col-reverse gap-2 items-end pr-4 pb-1 ${className}`;
  let t9;
  if ($[13] !== isExpanded) {
    t9 = () => {
      setIsExpanded(!isExpanded);
    };
    $[13] = isExpanded;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  const t10 = `${fabClasses} rotate-0`;
  const t11 = isExpanded ? "Hide map controls" : "Show map controls";
  let t12;
  if ($[15] !== isExpanded) {
    t12 = isExpanded ? <span className="text-xl leading-none">×</span> : <MoreHorizontal className="w-6 h-6" />;
    $[15] = isExpanded;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  let t13;
  if ($[17] !== isExpanded || $[18] !== t10 || $[19] !== t11 || $[20] !== t12 || $[21] !== t9) {
    t13 = <Button variant="ghost" onPress={t9} className={t10} size="icon" aria-label={t11} aria-expanded={isExpanded} aria-controls="map-controls-menu">{t12}</Button>;
    $[17] = isExpanded;
    $[18] = t10;
    $[19] = t11;
    $[20] = t12;
    $[21] = t9;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  const t14 = isExpanded ? "Hide map controls" : "Show map controls";
  let t15;
  if ($[23] !== t14) {
    t15 = <Tooltip placement="left">{t14}</Tooltip>;
    $[23] = t14;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  let t16;
  if ($[25] !== t13 || $[26] !== t15) {
    t16 = <TooltipTrigger>{t13}{t15}</TooltipTrigger>;
    $[25] = t13;
    $[26] = t15;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  let t17;
  if ($[28] !== buttonClasses || $[29] !== centerOnLocation || $[30] !== currentMapType || $[31] !== isExpanded || $[32] !== isFullscreen || $[33] !== map || $[34] !== toggleFullscreen || $[35] !== toggleMapType || $[36] !== zoomIn || $[37] !== zoomOut) {
    t17 = isExpanded && <div id="map-controls-menu" className="flex flex-col gap-2 items-end animate-in fade-in slide-in-from-bottom-2 zoom-in-95 duration-300"><TooltipTrigger><Button onPress={toggleMapType} isDisabled={!map} variant="ghost" size="icon" className={buttonClasses} aria-label={`Switch to ${currentMapType === "roadmap" ? "satellite" : currentMapType === "satellite" ? "hybrid" : currentMapType === "hybrid" ? "terrain" : "roadmap"} view`}>{currentMapType === "roadmap" ? <Satellite className="w-5 h-5" /> : currentMapType === "satellite" ? <MapIcon className="w-5 h-5" /> : currentMapType === "hybrid" ? <Eye className="w-5 h-5" /> : <MapIcon className="w-5 h-5" />}</Button><Tooltip placement="left">{`Switch to ${currentMapType === "roadmap" ? "satellite" : currentMapType === "satellite" ? "hybrid" : currentMapType === "hybrid" ? "terrain" : "roadmap"} view`}</Tooltip></TooltipTrigger><TooltipTrigger><Button onPress={toggleFullscreen} isDisabled={!map} variant="ghost" size="icon" className={buttonClasses} aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}><Maximize className="w-5 h-5" /></Button><Tooltip placement="left">{isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}</Tooltip></TooltipTrigger><TooltipTrigger><Button onPress={centerOnLocation} isDisabled={!map} variant="ghost" size="icon" className={buttonClasses} aria-label="Center on my location"><MapPin className="w-5 h-5" /></Button><Tooltip placement="left">Center on my location</Tooltip></TooltipTrigger><TooltipTrigger><Button onPress={zoomIn} isDisabled={!map} variant="ghost" size="icon" className={buttonClasses} aria-label="Zoom in"><ZoomIn className="w-5 h-5" /></Button><Tooltip placement="left">Zoom in</Tooltip></TooltipTrigger><TooltipTrigger><Button onPress={zoomOut} isDisabled={!map} variant="ghost" size="icon" className={buttonClasses} aria-label="Zoom out"><ZoomOut className="w-5 h-5" /></Button><Tooltip placement="left">Zoom out</Tooltip></TooltipTrigger></div>;
    $[28] = buttonClasses;
    $[29] = centerOnLocation;
    $[30] = currentMapType;
    $[31] = isExpanded;
    $[32] = isFullscreen;
    $[33] = map;
    $[34] = toggleFullscreen;
    $[35] = toggleMapType;
    $[36] = zoomIn;
    $[37] = zoomOut;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] !== t16 || $[40] !== t17 || $[41] !== t8) {
    t18 = <MapControl position={ControlPosition.RIGHT_BOTTOM}><div className={t8}>{t16}{t17}</div></MapControl>;
    $[39] = t16;
    $[40] = t17;
    $[41] = t8;
    $[42] = t18;
  } else {
    t18 = $[42];
  }
  return t18;
}
function _temp(error) {
  logger.debug("Error getting location for map centering:", error);
}